import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useConnection } from '@sezenta/connection';
import { Contract } from '../../context/ContractHelpers';
import {
  FIELD_LOGIN_PERMISSIONS,
  FIELD_ONLY_KEYS,
  MERGED_AWAY_KEYS,
  MOBILE_PERMISSION_VALUES,
  PERMISSION_MERGES,
  RECORD_COLLECTIONS_BUNDLE,
  permissionLabel,
} from '../../auth/permissionMeta';
import { AdminLink, AdminPageHeader, TEAL, cardShadow } from './adminUi';

interface Role {
  id: string;
  name?: string;
  permissions?: string[];
  status?: boolean;
}
interface PermissionItem {
  key: string;
  value: string;
}

// Group the flat permission catalog into readable sections, derived from the
// permission's enum KEY (e.g. EDIT_USER -> "User management"). Order here is the
// display order; the first matching rule wins, with an "Other" catch-all.
const GROUPS: { label: string; match: (key: string) => boolean }[] = [
  {
    label: 'System & access',
    match: (k) =>
      ['ADMIN_PORTAL', 'MANAGE_SYSTEM_SETTINGS', 'VIEW_ANALYTICS'].includes(k),
  },
  { label: 'User management', match: (k) => k.includes('USER') },
  { label: 'Driver management', match: (k) => k.includes('DRIVER') },
  { label: 'Sites & schedules', match: (k) => k.includes('SITE') },
  { label: 'Master data', match: (k) => k.includes('MASTER_DATA') },
  {
    label: 'Reporting',
    match: (k) =>
      k.includes('REPORT') || k.includes('APPROVE') || k.includes('EXPORT'),
  },
  {
    label: 'Field app',
    match: (k) => k.includes('BIN') || k.includes('ISSUE'),
  },
];
const groupLabel = (key: string) =>
  GROUPS.find((g) => g.match(key))?.label ?? 'Other';

// Bordered, searchable, grouped permission selector. Designed for the Form.Item
// value/onChange contract (antd injects them), so it stays bound to the form.
// Shows an "X of Y selected" count + Select all / Clear so a first-time user can
// see the full catalog size and that the list scrolls.
const PermissionPicker: React.FC<{
  catalog: PermissionItem[];
  value?: string[];
  onChange?: (v: string[]) => void;
}> = ({ catalog, value = [], onChange }) => {
  const [q, setQ] = useState('');
  const selected = new Set(value);
  const catalogValues = catalog.map((p) => p.value);
  // Rows the admin actually sees — merged-away permissions (e.g. "report a failed
  // collection") are folded into their primary and never shown on their own.
  const visibleCatalog = catalog.filter((p) => !MERGED_AWAY_KEYS.has(p.key));
  const total = visibleCatalog.length;
  const selectedCount = visibleCatalog.filter((p) =>
    selected.has(p.value),
  ).length;

  const keyToValue = new Map(catalog.map((p) => [p.key, p.value] as const));
  const mergedValuesOf = (key: string): string[] =>
    (PERMISSION_MERGES[key] ?? [])
      .map((k) => keyToValue.get(k))
      .filter((v): v is string => !!v);

  const writeBack = (next: Set<string>) =>
    onChange?.(catalogValues.filter((v) => next.has(v)));

  // Toggling a row flips the permission AND anything merged into it, so
  // "Record collections" grants/removes "report a failed collection" together.
  const toggle = (item: PermissionItem) => {
    const next = new Set(value);
    const targets = [item.value, ...mergedValuesOf(item.key)];
    const turningOn = !next.has(item.value);
    targets.forEach((v) => (turningOn ? next.add(v) : next.delete(v)));
    writeBack(next);
  };
  const setAll = (on: boolean) => onChange?.(on ? catalogValues : []);

  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? visibleCatalog.filter((p) => {
        const { label, description } = permissionLabel(p.key, p.value);
        return (
          label.toLowerCase().includes(needle) ||
          (description ?? '').toLowerCase().includes(needle) ||
          p.value.toLowerCase().includes(needle)
        );
      })
    : visibleCatalog;

  // Split the list into two self-explanatory platform sections instead of asking
  // the admin to pick a "role type" up front: 📱 Mobile app (driver actions) and
  // 💻 Web portal (everything else). They just tick what the role should do.
  const sections = [
    {
      label: '📱 Mobile app',
      hint: 'For drivers using the field app',
      items: filtered.filter((p) => FIELD_ONLY_KEYS.has(p.key)),
    },
    {
      label: '💻 Web portal',
      hint: 'For office staff using the admin portal',
      items: filtered.filter((p) => !FIELD_ONLY_KEYS.has(p.key)),
    },
  ].filter((s) => s.items.length > 0);

  return (
    <div className="border border-[#EAECF0] rounded-[10px] overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-[#F7F9FB] border-b border-[#EAECF0]">
        <span className="text-[13px] font-medium text-[#5E6870]">
          {selectedCount} of {total} selected
        </span>
        <span>
          <Button
            type="link"
            size="small"
            className="!px-1"
            disabled={selectedCount === total || total === 0}
            onClick={() => setAll(true)}
          >
            Select all
          </Button>
          <span className="text-[#D0D5DD]">·</span>
          <Button
            type="link"
            size="small"
            className="!px-1"
            disabled={selectedCount === 0}
            onClick={() => setAll(false)}
          >
            Clear
          </Button>
        </span>
      </div>

      <div className="px-3 py-2 border-b border-[#EAECF0]">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined className="text-[#84919A]" />}
          placeholder="Search permissions"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="max-h-[calc(100vh-230px)] min-h-[220px] overflow-auto">
        {total === 0 ? (
          <div className="text-[12px] text-[#84919A] py-6 text-center">
            No permissions available to assign.
          </div>
        ) : sections.length === 0 ? (
          <div className="text-[12px] text-[#84919A] py-6 text-center">
            No permissions match “{q}”.
          </div>
        ) : (
          sections.map((s) => (
            <div key={s.label}>
              <div className="sticky top-0 z-[1] px-3 py-2 bg-[#F1F5F6] border-b border-[#E4E9EB] flex items-baseline gap-2">
                <span className="text-[13px] font-semibold text-[#26A3A7]">
                  {s.label}
                </span>
                <span className="text-[11px] text-[#84919A]">{s.hint}</span>
              </div>
              {s.items.map((p) => {
                const on = selected.has(p.value);
                const { label, description, detail } = permissionLabel(
                  p.key,
                  p.value,
                );
                return (
                  <button
                    type="button"
                    key={p.value}
                    onClick={() => toggle(p)}
                    className={`w-full flex items-start gap-2.5 px-3 py-2 text-left border-b border-[#F5F6F7] hover:bg-[#F7F9FB] ${
                      on ? 'bg-[#F0FAFA]' : ''
                    }`}
                  >
                    <Checkbox
                      checked={on}
                      tabIndex={-1}
                      style={{ pointerEvents: 'none' }}
                      className="mt-0.5"
                    />
                    <span className="flex flex-col">
                      <span className="flex items-center gap-1.5 text-[13px] font-medium text-[#101828]">
                        {label}
                        {(detail || description) && (
                          <Tooltip
                            title={detail ?? description}
                            placement="right"
                          >
                            <InfoCircleOutlined
                              className="text-[#98A2B3] text-[12px] hover:text-[#26A3A7]"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Tooltip>
                        )}
                      </span>
                      {description && (
                        <span className="text-[11px] text-[#667085] leading-tight">
                          {description}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// CR004: per-contract role management. Roles live under a contract (Role PK =
// businessId + id), so pick a contract first, then build its roles over the
// permission catalog. Maps to estimate item 22.
const RolesPermissions: React.FC = () => {
  const connection = useConnection();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [contractId, setContractId] = useState<string | undefined>();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [contractsLoading, setContractsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [viewRole, setViewRole] = useState<Role | null>(null);
  const [editing, setEditing] = useState<Role | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [form] = Form.useForm();
  const watchedPermissions = (Form.useWatch('permissions', form) ??
    []) as string[];
  const [searchParams] = useSearchParams();

  useEffect(() => {
    setContractsLoading(true);
    connection
      .get('contracts', { params: { limit: 200, includeInactive: 'true' } })
      .then((r) => {
        const list: Contract[] = r.data?.data ?? [];
        setContracts(list);
        // Pre-select the project passed via ?projectId= (e.g. arriving from the
        // Users "no roles" hint) when it exists; otherwise fall back to the first.
        const wanted = searchParams.get('projectId');
        const preferred =
          wanted && list.some((c) => c.businessId === wanted)
            ? wanted
            : list[0]?.businessId;
        if (!contractId && preferred) setContractId(preferred);
      })
      .catch(() => undefined)
      .finally(() => setContractsLoading(false));
    connection
      .get('permissions')
      .then((r) => setPermissions(r.data ?? []))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection]);

  const loadRoles = useCallback(() => {
    if (!contractId) return;
    setLoading(true);
    // Return the promise so callers (e.g. seed) can await the refresh.
    return connection
      .get(`contracts/${contractId}/roles`)
      .then((r) => setRoles(r.data ?? []))
      .catch(() => message.error('Failed to load roles'))
      .finally(() => setLoading(false));
  }, [connection, contractId]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ permissions: [] });
    setOpen(true);
  };
  const openEdit = (role: Role) => {
    setEditing(role);
    form.setFieldsValue({
      name: role.name,
      permissions: role.permissions ?? [],
    });
    setOpen(true);
  };

  const submit = async () => {
    // Validate first; on failure antd already shows the inline errors (e.g. the
    // required Role name), so just stop — don't let the rejection bubble up as an
    // "Uncaught (in promise)".
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        await connection.patch(
          `contracts/${contractId}/roles/${editing.id}`,
          values,
        );
        message.success('Role updated');
      } else {
        await connection.post(`contracts/${contractId}/roles`, values);
        message.success('Role created');
      }
      setOpen(false);
      loadRoles();
    } catch {
      message.error('Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (role: Role, force = false) => {
    try {
      await connection.delete(
        `contracts/${contractId}/roles/${role.id}${force ? '?force=true' : ''}`,
      );
      message.success('Role deleted');
      loadRoles();
    } catch (e: any) {
      // 409 = role is still assigned to users. Confirm an explicit detach-and-delete.
      const status = e?.response?.status;
      const affected = e?.response?.data?.usersAffected as number | undefined;
      if (status === 409 && !force) {
        Modal.confirm({
          title: 'This role is in use',
          icon: <ExclamationCircleOutlined />,
          okText: 'Delete anyway',
          okButtonProps: { danger: true },
          cancelText: 'Cancel',
          content: (
            <span>
              It is assigned to{' '}
              <strong>
                {affected ?? 'some'} user{affected === 1 ? '' : 's'}
              </strong>
              . Deleting it will detach it from them and revoke the permissions
              it granted. This can’t be undone.
            </span>
          ),
          onOk: () => remove(role, true),
        });
      } else {
        message.error('Delete failed');
      }
    }
  };

  // Backfill the standard role set (cloned from the default contract) into a
  // contract that has none — e.g. ones created before auto-seeding.
  const seedDefaults = async () => {
    if (!contractId) return;
    setSeeding(true);
    try {
      // Generous timeout: a cold-start clone of the default role set can exceed the
      // default request timeout while completing server-side (→ "error but data on
      // refresh").
      const res = await connection.post(
        `contracts/${contractId}/seed-roles`,
        {},
        { timeout: 60000 },
      );
      message.success(`Added ${res.data?.created ?? 0} default role(s)`);
      // Await the refresh so the seed button stays disabled until the table
      // updates — prevents a re-click window that would re-seed.
      await loadRoles();
    } catch {
      message.error('Could not seed default roles');
    } finally {
      setSeeding(false);
    }
  };

  const columns = [
    { title: 'Role', dataIndex: 'name', key: 'name' },
    {
      title: 'Permissions',
      key: 'permissions',
      render: (_: any, r: Role) => (
        <span className="text-[#5E6870]">
          {r.permissions?.length ?? 0} permission
          {(r.permissions?.length ?? 0) === 1 ? '' : 's'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center' as const,
      render: (_: any, r: Role) => (
        <Space size="middle" onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Edit role" placement="bottom">
            <Button
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => openEdit(r)}
              style={{ color: TEAL, borderColor: TEAL }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this role?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => remove(r)}
          >
            <Tooltip title="Delete role" placement="bottom">
              <Button shape="circle" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="w-full h-full p-6 overflow-auto bg-[#F7F9FB]">
      <AdminPageHeader
        title="Roles & Permissions"
        subtitle="Define custom roles per project over the permission catalog."
        action={
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={openCreate}
            disabled={!contractId}
            style={{ background: TEAL }}
          >
            New Role
          </Button>
        }
      />

      <div className="mb-4 flex items-center gap-2">
        <span className="text-[#5E6870] text-[14px]">Project</span>
        <Select
          style={{ minWidth: 280 }}
          value={contractId}
          onChange={setContractId}
          notFoundContent={
            <div className="py-3 px-3 text-center text-[12px] text-[#84919A]">
              No projects yet. Create one in the{' '}
              <AdminLink to="/admin/projects">Projects</AdminLink> section.
            </div>
          }
          options={contracts.map((c) => ({
            value: c.businessId,
            label: c.name ?? c.businessId,
          }))}
          placeholder="Select a project"
        />
      </div>

      {contractsLoading ? (
        <div className="flex justify-center items-center py-16">
          <Spin />
        </div>
      ) : !contractId ? (
        <Empty description="Select a project to manage its roles" />
      ) : (
        <div
          className="bg-white rounded-[10px] border border-[#EAECF0] p-2"
          style={cardShadow}
        >
          <Table
            rowKey="id"
            loading={loading}
            dataSource={roles}
            columns={columns}
            scroll={{ x: 'max-content' }}
            onRow={(record) => ({
              onClick: () => setViewRole(record),
              style: { cursor: 'pointer' },
            })}
            pagination={false}
            locale={{
              // While loading, fall back to a blank cell (the spinner covers it)
              // so the seed CTA doesn't flash before roles arrive.
              emptyText: loading ? (
                <span />
              ) : (
                <div className="py-8 flex flex-col items-center gap-3">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <span className="text-[#5E6870]">
                        This project has no roles yet
                      </span>
                    }
                  />
                  <div className="text-[12px] text-[#84919A] max-w-[320px] text-center -mt-1">
                    Start the standard set (Admin, Driver, …) and tweak from
                    there, or build your own with{' '}
                    <span className="font-medium text-[#5E6870]">New Role</span>
                    .
                  </div>
                  <Space>
                    <Button
                      onClick={seedDefaults}
                      loading={seeding}
                      style={{ background: TEAL }}
                      type="primary"
                    >
                      Seed default roles
                    </Button>
                    <Button onClick={openCreate} icon={<PlusOutlined />}>
                      New Role
                    </Button>
                  </Space>
                </div>
              ),
            }}
          />
        </div>
      )}

      <Drawer
        title={editing ? 'Edit Role' : 'New Role'}
        open={open}
        onClose={() => setOpen(false)}
        width={460}
        extra={
          <Button
            type="primary"
            loading={submitting}
            onClick={submit}
            style={{ background: TEAL }}
          >
            Save
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Role name"
            rules={[
              { required: true, message: 'Name is required' },
              {
                validator: (_, value) => {
                  if (!value || /\p{L}/u.test(value)) {
                    return Promise.resolve();
                  }

                  return Promise.reject(
                    new Error('Role name must contain at least one letter'),
                  );
                },
              },
            ]}
          >
            <Input placeholder="e.g. Manager" size="large" />
          </Form.Item>

          {/* Nudge when a role has SOME mobile access (e.g. See bin/bag locations)
              but not the core "Record collections" it needs to log into the app. */}
          {watchedPermissions.some((p) =>
            MOBILE_PERMISSION_VALUES.includes(p),
          ) &&
            !FIELD_LOGIN_PERMISSIONS.every((p) =>
              watchedPermissions.includes(p),
            ) && (
              <Alert
                type="warning"
                showIcon
                className="!mb-3"
                message="This role can't log into the mobile app yet"
                description="Drivers need “Record collections” to use the field app."
                action={
                  <Button
                    size="small"
                    onClick={() => {
                      const merged = Array.from(
                        new Set([
                          ...watchedPermissions,
                          ...RECORD_COLLECTIONS_BUNDLE,
                        ]),
                      );
                      form.setFieldsValue({ permissions: merged });
                    }}
                  >
                    Add required
                  </Button>
                }
              />
            )}

          <Form.Item
            name="permissions"
            label="Permissions"
            tooltip="Tick what this role can do. Mobile-app actions and web-portal actions are listed separately."
          >
            <PermissionPicker catalog={permissions} />
          </Form.Item>
        </Form>
      </Drawer>

      {/* Role details (read-only) */}
      <Drawer
        open={!!viewRole}
        onClose={() => setViewRole(null)}
        width={460}
        title="Role details"
        extra={
          viewRole && (
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                const r = viewRole;
                setViewRole(null);
                openEdit(r);
              }}
            >
              Edit role
            </Button>
          )
        }
      >
        {viewRole &&
          (() => {
            // Hide merged-away permissions (shown as part of their primary) so
            // "report a failed collection" doesn't appear as its own tag.
            const perms = (viewRole.permissions ?? []).filter((val) => {
              const item = permissions.find((p) => p.value === val);
              return !(item && MERGED_AWAY_KEYS.has(item.key));
            });
            // Group this role's permission values for display, in GROUPS order.
            const sections = [...GROUPS.map((g) => g.label), 'Other']
              .map((label) => ({
                label,
                items: perms.filter((val) => {
                  const item = permissions.find((p) => p.value === val);
                  return (item ? groupLabel(item.key) : 'Other') === label;
                }),
              }))
              .filter((s) => s.items.length > 0);

            return (
              <>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-14 h-14 rounded-xl bg-[#E6F4F4] text-[#26A3A7] flex items-center justify-center text-[24px] font-semibold">
                    {(viewRole.name ?? '?').trim().charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-[18px] font-semibold text-[#18191C]">
                      {viewRole.name}
                    </div>
                    <div className="text-[#84919A] text-[13px]">
                      {perms.length} permission{perms.length === 1 ? '' : 's'}
                    </div>
                  </div>
                </div>

                <div className="text-[13px] font-medium text-[#5E6870] mb-2">
                  Permissions
                </div>
                {sections.length ? (
                  <div className="flex flex-col gap-3">
                    {sections.map((s) => (
                      <div
                        key={s.label}
                        className="bg-[#F7F9FB] rounded-md p-3 border border-[#EAECF0]"
                      >
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-[#84919A] mb-2">
                          {s.label}
                        </div>
                        <Space size={[0, 4]} wrap>
                          {s.items.map((val) => {
                            const item = permissions.find(
                              (p) => p.value === val,
                            );
                            return (
                              <Tag key={val} color="cyan">
                                {permissionLabel(item?.key ?? '', val).label}
                              </Tag>
                            );
                          })}
                        </Space>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-[#84919A] text-[14px]">
                    This role has no permissions assigned.
                  </div>
                )}
              </>
            );
          })()}
      </Drawer>
    </div>
  );
};

export default RolesPermissions;
